import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

class SocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnecting = false;
    this.listeners = new Map();
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT authentication token
   */
  connect(token) {
    if (this.socket?.connected || this.isConnecting) {
      console.log('Socket already connected or connecting');
      return;
    }

    if (!token) {
      console.error('Cannot connect to socket without authentication token');
      return;
    }

    this.isConnecting = true;

    try {
      this.socket = io(API_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: this.reconnectDelay,
        reconnectionAttempts: this.maxReconnectAttempts,
        timeout: 20000,
      });

      this.setupEventHandlers();
      this.isConnecting = false;
    } catch (error) {
      console.error('Socket connection error:', error);
      this.isConnecting = false;
    }
  }

  /**
   * Set up default socket event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.notifyListeners('connection_status', { connected: true });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.notifyListeners('connection_status', { connected: false, reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.notifyListeners('connection_error', { error: 'Max reconnection attempts reached' });
      } else {
        // Exponential backoff
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
      }
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
      this.notifyListeners('socket_error', { error });
    });

    this.socket.on('connected', (data) => {
      console.log('Server confirmed connection:', data);
    });

    this.socket.on('user_connected', (data) => {
      console.log('User connected:', data.username);
    });

    this.socket.on('user_disconnected', (data) => {
      console.log('User disconnected:', data.username);
    });
  }

  /**
   * Join a specific room
   * @param {string} room - Room name to join
   */
  joinRoom(room) {
    if (!this.socket?.connected) {
      console.warn('Cannot join room: socket not connected');
      return;
    }

    this.socket.emit('join_room', { room });
  }

  /**
   * Leave a specific room
   * @param {string} room - Room name to leave
   */
  leaveRoom(room) {
    if (!this.socket?.connected) {
      console.warn('Cannot leave room: socket not connected');
      return;
    }

    this.socket.emit('leave_room', { room });
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.socket) {
      console.warn('Socket not initialized');
      return;
    }

    this.socket.on(event, callback);

    // Track listener for cleanup
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    if (!this.socket) return;

    this.socket.off(event, callback);

    // Remove from tracked listeners
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {*} data - Data to send
   */
  emit(event, data) {
    if (!this.socket?.connected) {
      console.warn('Cannot emit: socket not connected');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Notify internal listeners (for connection status changes)
   * @param {string} event - Event name
   * @param {*} data - Data to pass to listeners
   */
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in listener callback:', error);
        }
      });
    }
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.socket?.connected || false;
  }

  /**
   * Disconnect from socket server
   */
  disconnect() {
    if (this.socket) {
      console.log('Disconnecting socket');
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    }
  }

  /**
   * Request manual data refresh
   * @param {string} type - Type of data to refresh (default: 'all')
   */
  requestRefresh(type = 'all') {
    this.emit('request_refresh', { type });
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
